"use client";

import { NEXT_PUBLIC_WHATSAPP_PHONE } from "@guru/shared/config/env.client";
import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { ChatBubble } from "@/components/marketing/whatsapp-bits";
import { useSupportStatus } from "@/lib/marketing/support-hours";
import { WhatsAppIcon } from "./whatsapp-icon";

/** Three bouncing dots inside an incoming WhatsApp bubble. */
function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-1 rounded-lg rounded-tl-none bg-white px-3 py-2.5 shadow-sm dark:bg-[#202c33]">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="typing-dot size-1.5 rounded-full bg-[#8696a0]"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The contact card *is* the conversation. A greeting types itself in, the
 * visitor's draft mirrors live into an outgoing bubble with delivery ticks, and
 * tappable quick-replies seed common questions — so sending feels like picking
 * up a chat already in progress. The composer below builds the real wa.me link.
 * Reduced motion skips straight to the resolved conversation.
 */
export function WhatsAppContactCard() {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const { online } = useSupportStatus();

  const phoneNumber = NEXT_PUBLIC_WHATSAPP_PHONE;
  const defaultMsg =
    t.contact?.whatsappDefaultMessage ?? "Hi! I'd like to know more about EtheraGuru.";
  const greeting =
    t.contact?.whatsappCardBody ??
    "Have questions? We're here to help. Start a WhatsApp conversation and we'll respond as soon as possible.";
  const agentName = t.contact?.whatsappAgentName ?? "EtheraGuru Support";
  const availability = t.contact?.whatsappAvailability ?? "Mon - Sat, 12:30 PM - 9:30 PM IST";
  const messageLabel = t.contact?.messageLabel ?? "Your message";
  const messagePlaceholder = t.contact?.messagePlaceholder ?? "Type your message...";
  const startWhatsappChatCta = t.contact?.startWhatsappChatCta ?? "Start WhatsApp Chat";
  const quickStartTitle = t.contact?.quickStartTitle ?? "Not sure where to start? Tap a question:";
  const typingLabel = t.contact?.typingLabel ?? "typing…";
  const statusOnline = t.contact?.statusOnline ?? "Online now";

  const quickPrompts = [
    t.contact?.quickPromptPricing ?? "What will it cost for my centre?",
    t.contact?.quickPromptDemo ?? "Can I get a quick demo?",
    t.contact?.quickPromptSwitch ?? "How do I move from spreadsheets?",
  ];

  const [message, setMessage] = useState(defaultMsg);
  // Conversation phase: the agent "types" before the greeting lands.
  const [greeted, setGreeted] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setGreeted(true);
      return;
    }
    const timer = setTimeout(() => setGreeted(true), 1100);
    return () => clearTimeout(timer);
  }, [prefersReducedMotion]);

  if (!phoneNumber) return null;

  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  // Header subtitle reflects the live desk status once it resolves on the client.
  const headerSubtitle =
    online === false ? availability : online === true ? statusOnline : "online";

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
      {/* Header — avatar with a live presence dot */}
      <div className="flex items-center gap-3 bg-[#075E54] px-5 py-4 dark:bg-[#202c33]">
        <div className="relative">
          <div className="flex size-11 items-center justify-center rounded-full bg-white/20 text-base font-bold text-white">
            {agentName.charAt(0).toUpperCase()}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 flex size-3.5">
            {online !== false ? (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75 motion-reduce:hidden" />
            ) : null}
            <span
              className={cn(
                "relative inline-flex size-3.5 rounded-full ring-2 ring-[#075E54] dark:ring-[#202c33]",
                online === false ? "bg-amber-400" : "bg-[#25D366]"
              )}
            />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold leading-tight text-white">{agentName}</p>
          <p className="mt-0.5 truncate text-xs text-white/70">
            {greeted ? headerSubtitle : typingLabel}
          </p>
        </div>
        <WhatsAppIcon className="size-7 text-white/40" />
      </div>

      {/* Conversation surface — real WhatsApp wallpaper */}
      <div className="wa-wallpaper flex flex-col gap-2 bg-[#efeae2] px-4 py-4 dark:bg-[#0b141a]">
        <p className="mx-auto rounded-md bg-black/5 px-2.5 py-1 text-center text-[10px] font-medium text-[#54656f] dark:bg-white/10 dark:text-[#8696a0]">
          {availability}
        </p>

        <AnimatePresence mode="wait" initial={false}>
          {greeted ? (
            <motion.div
              key="greeting"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <ChatBubble direction="in" time="now">
                {greeting}
              </ChatBubble>
            </motion.div>
          ) : (
            <motion.div key="typing" exit={{ opacity: 0 }}>
              <TypingBubble />
            </motion.div>
          )}
        </AnimatePresence>

        {/* The visitor's draft, mirrored live as an outgoing message */}
        <AnimatePresence>
          {greeted && message.trim() ? (
            <motion.div
              layout={!prefersReducedMotion}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <ChatBubble direction="out" ticks="sent">
                <span className="whitespace-pre-wrap break-words">{message}</span>
              </ChatBubble>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Quick-reply chips — seed a common question with one tap */}
      <div className="border-t border-border px-5 pt-4">
        <p className="mb-2.5 text-xs font-medium text-muted-foreground">{quickStartTitle}</p>
        <div className="flex flex-wrap gap-2">
          {quickPrompts.map((prompt) => {
            const isActive = message === prompt;
            return (
              <button
                key={prompt}
                type="button"
                onClick={() => setMessage(prompt)}
                aria-pressed={isActive}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200",
                  isActive
                    ? "border-[#25D366] bg-[#25D366]/10 text-[#0b7a3e] dark:text-[#25D366]"
                    : "border-border bg-background text-muted-foreground hover:-translate-y-0.5 hover:border-[#25D366]/50 hover:text-foreground"
                )}
              >
                {prompt}
              </button>
            );
          })}
        </div>
      </div>

      {/* Composer + CTA */}
      <div className="px-5 py-5">
        <label
          htmlFor="wa-message"
          className="mb-1.5 block text-xs font-medium text-muted-foreground"
        >
          {messageLabel}
        </label>
        <textarea
          id="wa-message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={3}
          className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#25D366]/50"
          placeholder={messagePlaceholder}
        />

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1EBE5A] hover:shadow-lg active:translate-y-0 active:bg-[#19A94E]"
        >
          <WhatsAppIcon className="size-5 transition-transform duration-300 group-hover:scale-110" />
          {startWhatsappChatCta}
        </a>
      </div>
    </div>
  );
}
